import { getRequestConfig } from "./config.ts";
import { AbortError } from "./error/abort-error.ts";
import { NetworkError } from "./error/network-error.ts";
import type { HttpVerbs } from "./types.ts";
import { createAccessTokenHeader, emitError, parseResponse } from "./util.ts";

export const coreRequest = createRequest({
  getOrigin: () => getRequestConfig("kivraCoreOrigin"),
});

export function createRequest({
  getOrigin,
  fetch = globalThis.fetch,
}: {
  getOrigin: () => string;
  fetch?: Window["fetch"];
}): Request {
  const request =
    <A extends RequestArguments>(method: HttpVerbs) => (args: A) =>
      makeRequest({
        fetch,
        method,
        url: getOrigin() + args.path,
        payload: args.payload,
        throwOnError: args.throwOnError,
        headers: {
          ...createAccessTokenHeader(args.accessToken),
          // eslint-disable-next-line @typescript-eslint/no-misused-spread
          ...args.headers,
        },
      });

  return {
    get: request("get"),
    delete: request("delete"),
    post: request("post"),
    put: request("put"),
    patch: request("patch"),
  };
}

export async function makeRequest<T = any, Throw extends boolean = true>(
  args: MakeRequestArguments<Throw>,
): Promise<
  Throw extends true ? T
    : { body: T; error: null } | { body: null; error: Error }
>;
export async function makeRequest<
  T = any,
  ThrowOnError extends boolean = true,
>({
  method,
  url,
  headers,
  payload,
  throwOnError,
  signal,
  fetch = globalThis.fetch,
}: MakeRequestArguments<ThrowOnError>): Promise<
  T | { body: T | null; error: Error | null }
> {
  const { body, error } = await fetch(url, {
    headers: {
      ...(payload && { "Content-Type": "application/json; charset=utf-8" }),
      // eslint-disable-next-line @typescript-eslint/no-misused-spread
      ...(headers && headers),
    },
    method: method.toUpperCase(),
    ...(payload && {
      body: typeof payload === "object" ? JSON.stringify(payload) : payload,
    }),
    signal,
  })
    // error is not unknown => Fetch always throws Error
    // eslint-disable-next-line @typescript-eslint/use-unknown-in-catch-callback-variable
    .catch((error) => {
      if (error.name === "AbortError") {
        throw new AbortError(error.message);
      } else {
        throw new NetworkError(error.message, method, url);
      }
    })
    .then((r) => parseResponse<T>(r, method, url))
    .catch((error: unknown) => {
      emitError(error);
      throw error;
    })
    .then((body) => ({ body, error: null }))
    // error is not unknown => Fetch always throws Error
    // eslint-disable-next-line @typescript-eslint/use-unknown-in-catch-callback-variable
    .catch((error) => ({
      body: null,
      error,
    }));

  if (throwOnError === false) {
    return { body, error };
  } else if (error) {
    throw error;
  } else {
    return body as T;
  }
}

interface RequestArguments<ThrowOnError extends boolean = boolean>
  extends Omit<MakeRequestArguments<ThrowOnError>, "method" | "fetch" | "url"> {
  /**
   * If given, corresponding header will be added
   */
  accessToken?: string;
  /**
   * The resorce path
   */
  path: string;
}

type Request = {
  [K in HttpVerbs]: <T = unknown, ThrowOnError extends boolean = true>(
    args: K extends "get" | "delete"
      ? Omit<RequestArguments<ThrowOnError>, "payload">
      : RequestArguments<ThrowOnError>,
  ) => ResponseType<T, ThrowOnError>;
};

interface MakeRequestArguments<ThrowOnError extends boolean> {
  /**
   * The request method, e.g., get, post
   */
  method: HttpVerbs;
  /**
   * URL of the resource you want to request
   */
  url: string;
  /**
   * Payload to send in the body of the request
   */
  payload?: Record<string, any> | string;
  /**
   * Any additional headers
   */
  headers?: Headers | Record<string, string>;
  /**
   * If true (default), an error will be thrown on failure.
   * If false, a boxed value will be returnd
   */
  throwOnError?: ThrowOnError;
  /**
   * A function to use to make the fetch call.
   */
  fetch?: Window["fetch"];
  /**
   * An AbortSignal to set request's signal.
   * Typical for cancel the request.
   */
  signal?: AbortSignal;
}

type ResponseType<T = any, Throw extends boolean = true> = Promise<
  Throw extends true ? T
    : { body: T; error: null } | { body: null; error: Error }
>;
